import { TsRestClient } from '@eluve/api-client-provider';
import { CorrelatedLogger } from '@eluve/logger';

/**
 * Result of asking the backend to reconcile a reserved PENDING_UPLOAD slot
 * against GCS truth (resolveReservedUpload, [ENG-4498]) before an orphan
 * cancel:
 *
 * - `recovered` / `cancelled_zero_byte` / `already_resolved` — the slot was
 *   resolved server-side from the stored bytes. The caller must SKIP its own
 *   cancel: the GCS-backed resolution is authoritative, and cancelling now
 *   could discard audio the backend just recovered.
 * - `left_pending` — GCS holds no finalized object, so the backend has no
 *   evidence either way. The caller may proceed with its existing cancel
 *   decision.
 * - `unavailable` — the endpoint is missing (backend not yet carrying
 *   ENG-4498), returned non-200, or the call failed. Callers fall back to
 *   their pre-guard behavior, which keeps this additive and skew-safe.
 */
export type ReservedUploadResolution =
  | 'recovered'
  | 'cancelled_zero_byte'
  | 'already_resolved'
  | 'left_pending'
  | 'unavailable';

export const isSlotResolvedAgainstGcs = (
  resolution: ReservedUploadResolution,
): boolean =>
  resolution === 'recovered' ||
  resolution === 'cancelled_zero_byte' ||
  resolution === 'already_resolved';

/**
 * Consults GCS truth before an orphan cancel. A missing local file proves
 * nothing about the upload: the bytes may have finalized in GCS before the
 * local state was lost (crash, teardown, IDB wipe). Cancelling such a slot
 * permanently discards recoverable audio — so every cancel path calls this
 * first and skips the cancel when the backend resolves the slot from stored
 * bytes. Never throws. [ENG-4562]
 */
export const resolveReservedUploadAgainstGcs = async (args: {
  apiClient: TsRestClient;
  log: CorrelatedLogger;
  tenantId: string;
  appointmentId: string;
  fileId: string;
  fileName: string;
}): Promise<ReservedUploadResolution> => {
  const { apiClient, log, tenantId, appointmentId, fileId, fileName } = args;

  try {
    const response = await apiClient.appointments.resolveReservedUpload({
      params: { tenantId, appointmentId, fileId },
      body: {},
    });

    if (response.status === 200) {
      return response.body.outcome;
    }

    // 404 covers both "row not visible to this caller" and an older backend
    // without the route; anything else is unexpected. Either way the caller
    // falls back to its existing cancel decision.
    log('info', 'resolveReservedUpload returned non-200 — falling back', {
      fileId,
      fileName,
      appointmentId,
      status: response.status,
    });
    return 'unavailable';
  } catch (error) {
    log('warn', 'resolveReservedUpload call failed — falling back', {
      fileId,
      fileName,
      appointmentId,
      error,
    });
    return 'unavailable';
  }
};
